import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { FileText, Image as ImageIcon } from "@phosphor-icons/react"
import { Banner } from "@/components/ui/banner"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
} from "@/components/ui/dialog"
import { ListRadioGroup, ListRadioItem } from "@/components/ui/list"
import { RadioGroup, RadioGroupItem, RadioGroupLabel } from "@/components/ui/radio-group"
import { FaroInsightsWrapper } from "@/grafana-faro"
import { ContactChannel, FileUploadRequestType } from "@/types/__generated__/union-types"

type SendRequestAfterSaving = { isSendRequestAfterSaving: boolean; documentType?: FileUploadRequestType }

type RequestUploadFilesDialogProps = {
  contactChannel?: ContactChannel
  isContactChannelVerified?: boolean
  isSendingRequest?: boolean
  sendRequestAfterSaving?: SendRequestAfterSaving
  onOpenChange: (open: boolean) => void
  onSendRequest?: (documentType?: FileUploadRequestType, onSendRequestCompleted?: () => void) => void
}

const FILE_REQUEST_KINDS = { documents: "documents", photos: "photos" } as const
type FileRequestKind = (typeof FILE_REQUEST_KINDS)[keyof typeof FILE_REQUEST_KINDS]

const PHOTO_FILE_UPLOAD_REQUEST_TYPES: FileUploadRequestType[] = [
  "PHOTOS_REQUIRED",
  "INSUFFICIENT_QUALITY_OF_PHOTOS",
  "AFFECTED_AREA_NOT_VISIBLE",
] as const
type PhotoFileUploadRequestType = (typeof PHOTO_FILE_UPLOAD_REQUEST_TYPES)[number]

const isFileRequestKind = (value: string): value is FileRequestKind => value in FILE_REQUEST_KINDS

const isPhotoFileUploadRequestType = (value: string): value is PhotoFileUploadRequestType =>
  PHOTO_FILE_UPLOAD_REQUEST_TYPES.some((photoFileUploadRequestType) => photoFileUploadRequestType === value)

interface DocumentTypeOptionProps {
  selectedFileRequestKind: FileRequestKind | undefined
  setSelectedFileRequestKind: (fileRequestKind: FileRequestKind) => void
  setSelectedPhotoFileUploadRequestType: (photoFileUploadRequestType: PhotoFileUploadRequestType | undefined) => void
}
const DocumentTypeOptions = (props: DocumentTypeOptionProps) => {
  const { selectedFileRequestKind, setSelectedFileRequestKind, setSelectedPhotoFileUploadRequestType } = props
  const { t } = useTranslation()

  const handleFileRequestKindChange = (fileRequestKind: FileRequestKind) => {
    setSelectedFileRequestKind(fileRequestKind)
    setSelectedPhotoFileUploadRequestType(undefined)
  }

  return (
    <>
      <p id="file-request-kind-label" className="text-on-surface-subdued text-sm">
        {t("patient.uploadFiles.fileRequestKind.label")}
      </p>
      <ListRadioGroup
        aria-labelledby="file-request-kind-label"
        className="gap-2"
        data-testid="file-request-kind-radio-group"
        value={selectedFileRequestKind}
        onValueChange={(value) => isFileRequestKind(value) && handleFileRequestKindChange(value)}>
        <ListRadioItem
          value="documents"
          data-testid="radio-DOCUMENT_REQUIRED"
          className="data-[state=checked]:bg-primary-surface data-[state=checked]:border-on-primary-surface w-full border">
          <div className="bg-surface-subdued flex h-10 w-10 shrink-0 items-center justify-center rounded-md border">
            <FileText className="text-on-surface" size={20} />
          </div>
          <div className="p-1 text-left">
            <div className="text-on-surface text-sm leading-5">
              {t("patient.uploadFiles.fileRequestKind.documents.title")}
            </div>
            <div className="text-on-surface-subdued text-xs leading-4 tracking-[0.25px]">
              {t("patient.uploadFiles.fileRequestKind.documents.subtitle")}
            </div>
          </div>
        </ListRadioItem>
        <ListRadioItem
          value="photos"
          data-testid="radio-photos"
          className="data-[state=checked]:bg-primary-surface data-[state=checked]:border-on-primary-surface w-full border">
          <div className="bg-surface-subdued flex h-10 w-10 shrink-0 items-center justify-center rounded-md border">
            <ImageIcon className="text-on-surface" size={20} />
          </div>
          <div className="p-1 text-left">
            <div className="text-on-surface text-sm leading-5">
              {t("patient.uploadFiles.fileRequestKind.photos.title")}
            </div>
            <div className="text-on-surface-subdued text-xs leading-4 tracking-[0.25px]">
              {t("patient.uploadFiles.fileRequestKind.photos.subtitle")}
            </div>
          </div>
        </ListRadioItem>
      </ListRadioGroup>
    </>
  )
}
const RequestUploadFilesDialog = (props: RequestUploadFilesDialogProps) => {
  const {
    contactChannel,
    isContactChannelVerified,
    isSendingRequest,
    sendRequestAfterSaving,
    onOpenChange,
    onSendRequest,
  } = props
  const { t } = useTranslation()
  const [selectedFileRequestKind, setSelectedFileRequestKind] = useState<FileRequestKind>()
  const [selectedPhotoFileUploadRequestType, setSelectedPhotoFileUploadRequestType] =
    useState<PhotoFileUploadRequestType>()

  const resolvedFileUploadRequestType = useMemo((): FileUploadRequestType | undefined => {
    if (selectedFileRequestKind === "documents") return "DOCUMENT_REQUIRED"
    if (selectedFileRequestKind === "photos") return selectedPhotoFileUploadRequestType
    return undefined
  }, [selectedFileRequestKind, selectedPhotoFileUploadRequestType])

  const handleCloseDialog = () => {
    onOpenChange(false)
    FaroInsightsWrapper.pushEvent("waiting_room_request_documents_close")
  }

  const handleSendRequest = () => {
    onSendRequest?.(resolvedFileUploadRequestType, () => onOpenChange(false))
    FaroInsightsWrapper.pushEvent("waiting_room_request_documents_send")
  }

  const isInvalidContactChannel =
    contactChannel === undefined ||
    contactChannel === "NO_CHANNEL" ||
    (contactChannel === "APP" && !isContactChannelVerified)

  const isSendRequestEnabled =
    !isInvalidContactChannel && resolvedFileUploadRequestType !== undefined && !isSendingRequest

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogPortal>
        <DialogOverlay onMouseDown={(e) => e.stopPropagation()} />
        <DialogContent
          data-testid="request-upload-files-dialog-content"
          className="flex min-h-50 flex-col"
          variant="small"
          onMouseDown={(e) => e.stopPropagation()}
          onPointerDownOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle>{t("patient.uploadFiles.requestDataFromPatient")}</DialogTitle>
          </DialogHeader>
          <div className="mx-6 my-2 grid gap-4 overflow-hidden">
            {isInvalidContactChannel && (
              <Banner variant="warning" type="inline" data-testid="no-channel-warning-banner">
                {t("patient.uploadFiles.noChannelHint")}
              </Banner>
            )}
            <div className="grid gap-2">
              <DocumentTypeOptions
                selectedFileRequestKind={selectedFileRequestKind}
                setSelectedFileRequestKind={setSelectedFileRequestKind}
                setSelectedPhotoFileUploadRequestType={setSelectedPhotoFileUploadRequestType}
              />
              {selectedFileRequestKind === "photos" && (
                <div className="border-border flex flex-col gap-2 border-l pt-2 pl-4">
                  <p id="photo-request-reason-label" className="text-on-surface-subdued text-sm">
                    {t("patient.uploadFiles.photoRequestReason.label")}
                  </p>
                  <RadioGroup
                    aria-labelledby="photo-request-reason-label"
                    data-testid="photo-request-reason-radio-group"
                    value={selectedPhotoFileUploadRequestType}
                    onValueChange={(value) =>
                      isPhotoFileUploadRequestType(value) && setSelectedPhotoFileUploadRequestType(value)
                    }>
                    {PHOTO_FILE_UPLOAD_REQUEST_TYPES.map((photoFileUploadRequestType) => (
                      <RadioGroupItem
                        key={photoFileUploadRequestType}
                        value={photoFileUploadRequestType}
                        id={`photo-request-reason-${photoFileUploadRequestType}`}
                        data-testid={`radio-${photoFileUploadRequestType}`}
                        className="data-[state=checked]:text-on-surface! h-auto w-full! border-0! bg-transparent! px-0 data-[state=checked]:border-0! data-[state=checked]:bg-transparent!">
                        <RadioGroupLabel
                          className="text-left font-normal"
                          htmlFor={`photo-request-reason-${photoFileUploadRequestType}`}>
                          <div className="text-on-surface text-sm leading-5">
                            {t(`patient.uploadFiles.photoRequestReason.${photoFileUploadRequestType}.title`)}
                          </div>
                          <div className="text-on-surface-subdued text-xs leading-4 tracking-[0.25px]">
                            {t(`patient.uploadFiles.photoRequestReason.${photoFileUploadRequestType}.subtitle`)}
                          </div>
                        </RadioGroupLabel>
                      </RadioGroupItem>
                    ))}
                  </RadioGroup>
                </div>
              )}
            </div>
            {!isInvalidContactChannel && sendRequestAfterSaving?.isSendRequestAfterSaving && (
              <Banner variant="warning" type="inline" data-testid="request-will-be-sent-banner">
                {t("patient.uploadFiles.requestWillBeSent")}
              </Banner>
            )}
          </div>
          <DialogFooter className="mt-auto flex p-6 pt-4" data-testid="request-upload-files-dialog-footer">
            <Button className="px-4 py-2" variant="secondary" data-testid="cancel-button" onClick={handleCloseDialog}>
              {t("common.button.cancel")}
            </Button>
            <Button
              className="px-4 py-2"
              onClick={handleSendRequest}
              data-testid="send-request-button"
              isLoading={isSendingRequest}
              disabled={!isSendRequestEnabled}>
              {t("common.button.sendRequest")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  )
}

export { RequestUploadFilesDialog, type SendRequestAfterSaving }
